<template>
  <div style="display: flex; width: 100%; height: 100%; position: relative">
    <ag-grid-vue
      ref="agTableRef"
      @firstDataRendered="firstDataRendered"
      @selectionChanged="cellSelectionChanged"
      @grid-ready="gridReadyFn"
      style="flex: 1"
      @cellDoubleClicked="cellDoubleClicked"
      @cellClicked="cellClicked"
      class="ag-theme-balham"
      v-bind="agGridOptions"
    ></ag-grid-vue>
    <!-- 框选矩形覆盖层 -->
    <div
      v-show="boxState.active && boxState.dragStarted"
      class="box-select-rectangle"
      :style="{
        left: boxState.rect.left + 'px',
        top: boxState.rect.top + 'px',
        width: boxState.rect.width + 'px',
        height: boxState.rect.height + 'px',
      }"
    ></div>
    <div
      v-if="showRightConfig"
      style="
        width: 18px;
        display: flex;
        flex-direction: column;
        border-bottom: 1px solid #cbd0d3;
        border-right: 1px solid #cbd0d3;
        border-top: 1px solid #cbd0d3;
      "
    >
      <span class="right-tool" @click="colClickFn('fit')">
        <el-icon style="transform: rotate(90deg);left:1px;    margin-bottom: 3px">
          <Operation />
        </el-icon>自适应列
      </span>
      <span class="right-tool" @click="colClickFn('reset')">
        <el-icon style="left:1px;margin-bottom: 3px">
          <Operation />
        </el-icon>重置列
      </span>
       <span class="right-tool" @click="colClickFn('copy')">
        <el-icon style="left:1px;margin-bottom: 3px">
          <Operation />
        </el-icon>复制列
      </span>
        <span class="right-tool" @click="colClickFn('showField')">
        <el-icon style="left:1px;margin-bottom: 3px">
          <Operation />
        </el-icon>显示列
      </span>
    </div>
    
    <!-- 修改：添加复制范围选择 -->
    <el-dialog
      v-model="showColumnDialog"
      title="选择要复制的列"
      width="600"
      center
      :close-on-click-modal="false"
    >
      <div style="padding:0 20px;">
      <!-- 列选择 -->
      <div class="mb-3">
        <label class="form-label">选择列字段：</label>
        <el-radio-group v-model="selectedColumn">
          <el-radio
            v-for="column in availableColumns"
            :key="column.field"
            :label="column.field"
            style="display: block; margin-bottom: 10px;width: 100px;"
          >
            {{ column.headerName }}
          </el-radio>
        </el-radio-group>
      </div>
      
      <!-- 添加：复制范围选择 -->
      <div class="mb-3">
        <label class="form-label">复制范围：</label>
        <el-radio-group v-model="copyRangeType" @change="onCopyRangeChange">
          <el-radio label="selected">选中行数据</el-radio>
          <el-radio label="currentPage">当前页数据</el-radio>
          <el-radio label="custom">自定义范围</el-radio>
        </el-radio-group>
      </div>
      
      <!-- 添加：自定义范围输入框 -->
      <div v-if="copyRangeType === 'custom'" class="custom-range-input mb-3">
        <el-input-number
          v-model="customRangeStart"
          :min="1"
          :max="totalRows"
          label="开始行"
          class="w-32"
        ></el-input-number>
        <span class="range-separator">至</span>
        <el-input-number
          v-model="customRangeEnd"
          :min="customRangeStart"
          :max="totalRows"
          label="结束行"
          class="w-32"
        ></el-input-number>
        <span class="total-rows-text">(共 {{ totalRows }} 行)</span>
      </div>
      
      <!-- 添加：选中行提示 -->
      <div v-if="copyRangeType === 'selected' && selectedRows.length === 0" class="warning-text">
        提示：当前没有选中的行，请先在表格中选择行数据
      </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showColumnDialog = false">取消</el-button>
          <el-button 
            type="primary" 
            @click="copySelectedColumn"
            :disabled="!canCopy"
          >确定复制</el-button>
        </div>
      </template>
    </el-dialog>
    <configShowFieldDialog
      ref="configShowFieldRef"
      @success="handleColumnConfigSuccess"
    />
  </div>
</template>
<script setup lang="ts">
// 注册aggrid表格组件
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AgGridVue } from "ag-grid-vue3";
import { reactive, watch, defineEmits, ref, inject, computed, onBeforeUnmount, nextTick } from "vue";
import { ElMessage, ElDialog, ElRadioGroup, ElRadio, ElButton, ElInputNumber } from "element-plus";
import buttonList from "./opeList.vue";
import configShowFieldDialog from './configShowFieldDialog.vue'

const agTableRef = ref();
const configShowFieldRef = ref();
const emit = defineEmits(["selectRows", "cellClicked"]);
const props = defineProps([
  "columnDefs",
  "rowData",
  "size",
  "showRightConfig",
  "showTenant",
  "hideCheckBox",
  "pageKey",
  "enableBoxSelect"
]);

// 弹窗相关的状态
const showColumnDialog = ref(false);
const selectedColumn = ref('');
const availableColumns = ref([]);

// 新增：复制范围相关状态
const copyRangeType = ref('selected'); // selected, currentPage, custom
const customRangeStart = ref(1);
const customRangeEnd = ref(10);
const totalRows = ref(0);

// 计算属性：是否可以复制
const canCopy = computed(() => {
  if (!selectedColumn.value) return false;
  
  if (copyRangeType.value === 'selected') {
    return selectedRows.value.length > 0;
  } else if (copyRangeType.value === 'custom') {
    return customRangeStart.value <= customRangeEnd.value && 
           customRangeStart.value >= 1 && 
           customRangeEnd.value <= totalRows.value;
  }
  
  return true; // currentPage 模式下只要有列选中就可以复制
});

// 选中行数量（用于显示提示）
const selectedRows = ref([]);
const cellSelectionChanged = ({ api }) => {
  const selected = api.getSelectedRows();
  selectedRows.value = selected;
  emit("selectRows", selected);
};
async function universalCopy(text) {
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn("Clipboard API 失败");
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand("copy");
  } catch (err) {
    console.error("复制失败:", err);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}
function toFitColumunsFn() {
  let tWidth = 0;
  const { columnApi, api } = gParams;
  const el = agTableRef.value.$el;
  columnApi.autoSizeAllColumns();
  (columnApi.getColumnState() || []).forEach(o => {
    if (o.hide === false) {
      tWidth += o.width;
    }
  });
  const { width } = el.getBoundingClientRect();
  if (tWidth < width) {
    api.sizeColumnsToFit();
  }
}
const cellClicked = p => {
  emit("cellClicked", p);
};
const cellDoubleClicked = async p => {
  const {
    colDef: { field, valueFormatter = null },
    data
  } = p;
  if (field && data[field]) {
    let s = data[field];
    if (valueFormatter) {
      s = valueFormatter(p);
    }
    const success = await universalCopy(s);
    success && ElMessage.success("复制成功");
  }
};

// 新增：复制范围改变时的处理
const onCopyRangeChange = () => {
  // 当切换到自定义范围时，设置默认值
  if (copyRangeType.value === 'custom') {
    customRangeStart.value = 1;
    customRangeEnd.value = Math.min(10, totalRows.value);
  }
};

// 修改：复制选中列的数据，支持多种范围
const copySelectedColumn = async () => {
  if (!selectedColumn.value || !canCopy.value) {
    return;
  }
  
  const { api } = gParams;
  let targetRows = [];
  
  // 根据选择的范围获取数据行
  switch (copyRangeType.value) {
    case 'selected':
      // 获取选中的行
      targetRows = api.getSelectedRows();
      break;
    case 'currentPage':
      // 获取当前页显示的行
      targetRows = props.rowData
      break;
    case 'custom':
      // 获取自定义范围的行（注意索引从0开始，所以需要减1）
      targetRows =props.rowData.slice(customRangeStart.value - 1, customRangeEnd.value);
      break;
    default:
      ElMessage.warning("请选择有效的复制范围");
      return;
  }
  
  if (targetRows.length === 0) {
    ElMessage.warning("没有数据可复制");
    showColumnDialog.value = false;
    return;
  }
  
  // 找到选中列的定义
  const columnDef = availableColumns.value.find(col => col.field === selectedColumn.value);
  if (!columnDef) return;
  
  // 收集指定范围内该列的数据
  const columnData = targetRows.map((data, index) => {
    const value = data[selectedColumn.value];
    
    // 应用值格式化器（如果有）
    if (columnDef.valueFormatter && value !== undefined && value !== null) {
      return columnDef.valueFormatter({
        value,
        data,
        colDef: columnDef,
        rowIndex: index
      });
    }
    return value || '';
  });
  
  // 拼接成文本并复制
  const textToCopy = columnData.join('\n');
  const success = await universalCopy(textToCopy);
  
  if (success) {
    ElMessage.success(`成功复制 ${columnData.length} 条数据`);
    showColumnDialog.value = false;
  } else {
    ElMessage.error("复制失败");
  }
};

const colClickFn = type => {
  if (type === "fit") {
    setTimeout(() => {
      // gParams.columnApi.autoSizeAllColumns();
      toFitColumunsFn();
    });
  } else if(type=='copy'){
    // 修改：打开列选择弹窗
    if (!props.rowData || props.rowData.length === 0) {
      ElMessage.warning("暂无数据可复制");
      return;
    }
    
    // 更新总行数
    totalRows.value = props.rowData.length;
    // 过滤出可用的列（排除操作列和复选框列）
    availableColumns.value = props.columnDefs
      .filter(col => 
        !col.cellRenderer && 
        !col.checkboxSelection && 
        col.field && 
        col.headerName!=='操作' &&
        col.headerName
      )
      .map(col => ({ ...col }));
    
    // 如果有可用列，默认选择第一个
    if (availableColumns.value.length > 0) {
      selectedColumn.value = availableColumns.value[0].field;
    }
    
    // 重置复制范围为当前页
    copyRangeType.value = 'selected';
    
    // 打开弹窗
    showColumnDialog.value = true;
  } else if(type=='showField'){
 // 显示列配置弹窗
    if (!gParams) return;
    
    // 过滤出可用的列（排除操作列和复选框列）
    const availableColumns = props.columnDefs
      .filter(col => 
        !col.checkboxSelection && 
        col.field && 
        col.headerName!=='操作' &&
        col.headerName&&
        col.field !== 'tenantId' // 新增：排除租户列
      )
      .map(col => ({ ...col }));
    
    // 打开列配置弹窗
    configShowFieldRef.value.openDialog(availableColumns, props.pageKey||'0');
    
  } else {
    gParams.columnApi.applyColumnState({
      state: cacheColConfig,
      applyOrder: true
    });
    setTimeout(() => {
      gParams.columnApi.autoSizeAllColumns();
    });
  }
};
const tenantCb = inject("$tenantInfo") as any;
const getColumnDefs = () => {
  const tmpList = props.columnDefs.map(o => {
    const obj = { ...o };
    // 操作列固定
    if ("opeList" in o) {
      obj["cellRenderer"] = buttonList;
      obj["cellRendererParams"] = { list: o.opeList,showMoreMode: o.moreMode || false };
      obj["pinned"] = "right";
      obj["lockPosition"] = "right";
      obj["width"] = obj.width || (o.opeList.length * 70);
      obj["maxWidth"] = obj.width || (o.opeList.length * 70);
      obj["minWidth"] = obj.width || (o.opeList.length * 70);
    }
    return obj;
  });

  const tenantObj = tenantCb();
  if (tenantObj.isRootUser && props.showTenant) {
    const tenantCol = {
      headerName: "租户编码",
      field: "tenantId",
      width: 120,
      valueFormatter: ({ value }) => {
        const tmp = tenantObj.tenantList.find(o => o.id == value);
        return tmp ? tmp?.tenantCode : "";
      }
    };
    tmpList.unshift(tenantCol);
  }
  if (!props.hideCheckBox) {
    const checkBoxCol = {
      field: "",
      headerCheckboxSelection: true,
      checkboxSelection: true,
      pinned: "left",
      maxWidth: 45,
      lockPosition: "left"
    };
    tmpList.unshift(checkBoxCol);
  }
  return tmpList;
};
const agGridOptions = reactive({
  localeText: {
    noRowsToShow: "",
    filterOoo: "过滤...",
    equals: "等于",
    notEqual: "不等于",
    blank: "空白",
    notBlank: "非空白",
    empty: "请选择",
    contains: "包含",
    notContains: "不包含",
    startsWith: "开始于",
    endsWith: "结束于",
    andCondition: "并且",
    orCondition: "或者"
  },
  rowSelection: "multiple",
  suppressRowClickSelection: true,
  headerHeight: 28,
  rowHeight: 25,
  defaultColDef: {
    resizable: true,
    sortable: true,
    minWidth: 100,
    filter: true
  },
  columnDefs: getColumnDefs(),
  rowData: [],
  overlayNoRowsTemplate:
    props.size == "small"
      ? `<div class="empty-data empty-small-table">
        <div>暂无数据</div>
      </div>`
      : `<div class="empty-data ${
          props.size == "middle" ? "empty-middle-table" : ""
        }">
        <div class="empty-img"></div>
        <div>暂无数据</div>
      </div>`
});
let gParams = null;

// ==================== 框选功能 ====================
const DRAG_THRESHOLD = 5; // 最小拖拽距离（像素）
const AUTO_SCROLL_ZONE = 30; // 自动滚动触发区域（像素）
const AUTO_SCROLL_SPEED = 6; // 自动滚动速度

const boxState = reactive({
  active: false,
  dragStarted: false,
  startX: 0,
  startY: 0,
  // 按下时记录的「内容绝对坐标」锚点，滚动后仍指向同一行/列
  startAbsX: 0,
  startAbsY: 0,
  // 最近一次框选的内容绝对坐标范围（mouseup 时据此选中完整区间）
  absTop: 0,
  absBottom: 0,
  rect: { left: 0, top: 0, width: 0, height: 0 },
  highlightedNodes: [],
});

// 获取 AG Grid 的 body-viewport 元素
const getBodyViewport = () => {
  if (!agTableRef.value) return null;
  const gridEl = agTableRef.value.$el;
  return gridEl.querySelector('.ag-body-viewport');
};

// 获取 gridWrapper 的 boundingRect 用于定位覆盖层
const getGridElRect = () => {
  if (!agTableRef.value) return null;
  return agTableRef.value.$el.getBoundingClientRect();
};

// 框选 mousedown
const onBoxMouseDown = (e) => {
  if (e.button !== 0) return; // 仅左键
  // 排除复选框、按钮、链接、输入框
  const target = e.target;
  if (target.closest('.ag-selection-checkbox') ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('.ag-header')) {
    return;
  }

  const viewport = getBodyViewport();
  if (!viewport) return;

  const viewportRect = viewport.getBoundingClientRect();
  boxState.startX = e.clientX - viewportRect.left;
  boxState.startY = e.clientY - viewportRect.top;
  // 锚点用「内容绝对坐标」记录，滚动后仍指向同一行/列
  boxState.startAbsX = boxState.startX + viewport.scrollLeft;
  boxState.startAbsY = boxState.startY + viewport.scrollTop;
  boxState.active = true;
  boxState.dragStarted = false;
  boxState.rect = { left: 0, top: 0, width: 0, height: 0 };
  boxState.highlightedNodes = [];

  viewport.classList.add('box-selecting');
};

// 框选 mousemove
const onBoxMouseMove = (e) => {
  if (!boxState.active) return;

  const viewport = getBodyViewport();
  if (!viewport) return;

  const viewportRect = viewport.getBoundingClientRect();
  const currentX = e.clientX - viewportRect.left;
  const currentY = e.clientY - viewportRect.top;

  // 鼠标实际位移（仅用于判断是否超过最小拖拽阈值）
  const moveWidth = Math.abs(currentX - boxState.startX);
  const moveHeight = Math.abs(currentY - boxState.startY);

  if (!boxState.dragStarted) {
    const distance = Math.sqrt(moveWidth * moveWidth + moveHeight * moveHeight);
    if (distance < DRAG_THRESHOLD) return;
    boxState.dragStarted = true;
  }

  // 自动滚动：鼠标接近 viewport 边缘时滚动
  if (currentY < AUTO_SCROLL_ZONE) {
    viewport.scrollTop -= AUTO_SCROLL_SPEED;
  } else if (currentY > viewportRect.height - AUTO_SCROLL_ZONE) {
    viewport.scrollTop += AUTO_SCROLL_SPEED;
  }

  // 滚动后再读取统一的滚动偏移，保证后续坐标与当前渲染一致
  const scrollTop = viewport.scrollTop;
  const scrollLeft = viewport.scrollLeft;

  // 关键：用「内容绝对坐标」计算框选范围。锚点固定在按下时的那一行/列，
  // 无论 viewport 怎么滚动，起始位置都不会漂移，范围也会随滚动向下延伸
  const currentAbsX = currentX + scrollLeft;
  const currentAbsY = currentY + scrollTop;
  const absLeft = Math.min(boxState.startAbsX, currentAbsX);
  const absRight = Math.max(boxState.startAbsX, currentAbsX);
  const absTop = Math.min(boxState.startAbsY, currentAbsY);
  const absBottom = Math.max(boxState.startAbsY, currentAbsY);

  boxState.absTop = absTop;
  boxState.absBottom = absBottom;

  // 覆盖层裁剪到可视区域内：起始行滚出顶部时，矩形顶端贴着可视区上沿
  const gridRect = getGridElRect();
  if (gridRect) {
    const vpTop = Math.max(0, absTop - scrollTop);
    const vpBottom = Math.min(viewportRect.height, absBottom - scrollTop);
    const vpLeft = Math.max(0, absLeft - scrollLeft);
    const vpRight = Math.min(viewportRect.width, absRight - scrollLeft);
    boxState.rect = {
      left: vpLeft + (viewportRect.left - gridRect.left),
      top: vpTop + (viewportRect.top - gridRect.top),
      width: Math.max(0, vpRight - vpLeft),
      height: Math.max(0, vpBottom - vpTop),
    };
  }

  // 更新高亮行（直接传内容绝对坐标范围）
  updateHighlightedRows(absTop, absBottom);
};

// 按内容绝对坐标范围取出所有命中的行（含已滚出可视区但仍在数据中的行）
const getNodesInRange = (api, absTop, absBottom) => {
  const rowCount = api.getDisplayedRowCount();
  if (rowCount === 0) return [];
  const rowHeight = 25;
  // 行高固定，用绝对 Y 反推大致行索引，再各放宽一行用 rowTop 精确校验
  const startIdx = Math.max(0, Math.floor(absTop / rowHeight) - 1);
  const endIdx = Math.min(rowCount - 1, Math.floor(absBottom / rowHeight) + 1);
  const nodes = [];
  for (let i = startIdx; i <= endIdx; i++) {
    const node = api.getDisplayedRowAtIndex(i);
    if (!node) continue;
    const rowTop = node.rowTop ?? i * rowHeight;
    const rowH = node.rowHeight || rowHeight;
    if (rowTop + rowH >= absTop && rowTop <= absBottom) {
      nodes.push(node);
    }
  }
  return nodes;
};

// 框选 mouseup
const onBoxMouseUp = (e) => {
  if (!boxState.active) return;

  const viewport = getBodyViewport();
  if (viewport) {
    viewport.classList.remove('box-selecting');
  }

  // 按完整绝对范围选中，保证滚出可视区的行也能被选中（仅真正拖拽过才执行）
  if (gParams && boxState.dragStarted) {
    const { api } = gParams;
    const nodes = getNodesInRange(api, boxState.absTop, boxState.absBottom);
    if (nodes.length > 0) {
      // 未按 Ctrl/Meta 时先清除已有选择
      if (!e.ctrlKey && !e.metaKey) {
        api.deselectAll();
      }
      nodes.forEach(node => {
        node.setSelected(true, false);
      });
    }
  }

  // 清除高亮样式
  clearVisualHighlight();
  // 重置状态
  boxState.active = false;
  boxState.dragStarted = false;
  boxState.highlightedNodes = [];
  boxState.rect = { left: 0, top: 0, width: 0, height: 0 };
};

// 根据框选 Y 范围确定覆盖的行（参数为内容绝对坐标范围，仅高亮当前可见行）
const updateHighlightedRows = (absTop, absBottom) => {
  if (!gParams) return;
  const { api } = gParams;

  // 仅遍历可见行范围
  const firstRow = api.getFirstDisplayedRow();
  const lastRow = api.getLastDisplayedRow();

  const newHighlighted = [];
  for (let i = firstRow; i <= lastRow; i++) {
    const node = api.getDisplayedRowAtIndex(i);
    if (!node) continue;

    const rowTop = node.rowTop;
    const rowHeight = node.rowHeight || 25;
    const rowBottom = rowTop + rowHeight;

    if (rowBottom >= absTop && rowTop <= absBottom) {
      newHighlighted.push(node);
    }
  }

  boxState.highlightedNodes = newHighlighted;
  applyVisualHighlight(newHighlighted);
};

// 为高亮行添加 CSS 类
const applyVisualHighlight = (nodes) => {
  const viewport = getBodyViewport();
  if (!viewport) return;

  // 清除旧高亮
  viewport.querySelectorAll('.ag-row.box-select-highlight').forEach(el => {
    el.classList.remove('box-select-highlight');
  });

  // 添加新高亮
  nodes.forEach(node => {
    const rowEl = viewport.querySelector(`.ag-row[row-id="${node.id}"]`);
    if (rowEl) {
      rowEl.classList.add('box-select-highlight');
    }
  });
};

// 清除所有高亮样式
const clearVisualHighlight = () => {
  const viewport = getBodyViewport();
  if (!viewport) return;
  viewport.querySelectorAll('.ag-row.box-select-highlight').forEach(el => {
    el.classList.remove('box-select-highlight');
  });
};

// 绑定框选事件监听
const attachBoxSelectListeners = () => {
  const viewport = getBodyViewport();
  if (!viewport) return;

  viewport.addEventListener('mousedown', onBoxMouseDown);
  document.addEventListener('mousemove', onBoxMouseMove);
  document.addEventListener('mouseup', onBoxMouseUp);
};

// 解绑框选事件监听
const detachBoxSelectListeners = () => {
  const viewport = getBodyViewport();
  if (viewport) {
    viewport.removeEventListener('mousedown', onBoxMouseDown);
  }
  document.removeEventListener('mousemove', onBoxMouseMove);
  document.removeEventListener('mouseup', onBoxMouseUp);
};

onBeforeUnmount(() => {
  detachBoxSelectListeners();
});
// ==================== 框选功能 END ====================

const gridReadyFn = p => {
  gParams = p;
   // 网格加载完成后，从缓存加载列配置
  loadColumnConfigFromCache();
  // 框选功能：grid DOM 就绪后绑定事件
  if (props.enableBoxSelect) {
    nextTick(() => attachBoxSelectListeners());
  }
};

// 从缓存加载列配置
const loadColumnConfigFromCache = () => {
  if (!props.pageKey || !gParams) return;
  
  try {
    const cacheKey = 'column_config_map';
    const cachedMap = localStorage.getItem(cacheKey);
    if (cachedMap) {
      const columnConfigMap = JSON.parse(cachedMap);
      const config = columnConfigMap[props.pageKey||'0'];
      if (config) {
        applyColumnConfig(config);
      }
    }
  } catch (error) {
    console.error('加载列配置缓存失败:', error);
  }
};

// 应用列配置
const applyColumnConfig = (columnFields) => {
  if (!gParams || !columnFields) return;

  const { columnApi } = gParams;
  const allColumns = columnApi.getAllColumns();
  
  // 创建一个包含所有需要显示的列的列表
  let columnsToShow = [...columnFields];
  
  // 确保租户编码列始终在最前面
  if (props.showTenant) {
    // 先移除所有tenantId列（如果存在）
    columnsToShow = columnsToShow.filter(field => field !== 'tenantId');
    // 然后将其添加到数组开头
    columnsToShow.unshift('tenantId');
  }
    
  // 显示指定的列，隐藏其他列
  allColumns.forEach(col => {
    const colDef = col.getColDef();
    // 跳过操作列和复选框列
    if (colDef.checkboxSelection) return;
    
    const shouldShow = columnsToShow.includes(colDef.field);
    columnApi.setColumnVisible(colDef.field, shouldShow);
  });
  
   // 按照配置的顺序重新排列列
  setTimeout(() => {
    // 1. 首先确保租户编码列在最前面
    if (props.showTenant) {
      columnApi.moveColumn('tenantId', 0);
    }
    
    // 2. 然后处理其他列的顺序
    columnsToShow.forEach((field, index) => {
      // 跳过已经处理的tenantId列
      if (field !== 'tenantId') {
        // 计算实际位置（跳过tenantId列）
        const actualIndex = props.showTenant ? index +1 : index;
        columnApi.moveColumn(field, actualIndex);
      }
    });
    
    // 3. 最后调整列宽
    toFitColumunsFn();
  }, 100);
};
// 处理列配置成功回调
const handleColumnConfigSuccess = (columnFields) => {
  applyColumnConfig(columnFields);
  ElMessage.success('列配置已更新');
};

watch(
  () => props.rowData,
  list => {
    agGridOptions.rowData = list;
  },
  { deep: true, immediate: true }
);
let cacheColConfig = null;
const firstDataRendered = ({ columnApi }) => {
  setTimeout(() => {
    cacheColConfig = columnApi.getColumnState();
    toFitColumunsFn();
  });
};
</script>
<style>
.ag-header {
  height: 32px !important;
  line-height: 32px;
  min-height: 32px !important;
  border-color: #ddd;
  background: #efefef !important;
}

.ag-header-row .ag-header-cell {
  height: 32px !important;
}

.ag-root-wrapper {
  border-color: #ddd;
}

.ag-theme-balham .ag-cell:not(.ag-last-column),
.ag-theme-balham .ag-header-cell:not(.ag-last-column) {
  border-right: 1px solid #ddd;
  /* 调整颜色和粗细 */
}

.ag-theme-balham .ag-header-cell {
  background-color: #efefef;
  /* 表头背景色（示例为浅蓝色） */
  /* border-bottom: 1px solid #81c1f5; */
  /* 可选：底部边框 */
}

.ag-theme-balham .ag-header-cell-text {
  font-weight: bold;
  /* 表头文本加粗 */
  color: #666;
  /* 表头文字颜色 */
}

.ag-theme-balham .ag-cell {
  color: #666;
  /* 自定义字体颜色（示例为深灰色） */
}

.ag-root-wrapper-body.ag-layout-normal {
  height: auto;
  min-height: 56px;
}

.right-tool {
  writing-mode: vertical-rl;
  text-orientation: upright;
  letter-spacing: 1px;
  border-bottom: 1px solid #ddd;
  padding: 5px 1px;
  cursor: pointer;
  font-size: 12px;
}

.right-tool:hover {
  color: whitesmoke;
  background: #409eff;
  border-radius: 3px;
}

.empty-small-table {
  height: 100%;
  line-height: 80px;
}

.empty-img {
  background-image: url("@/assets/noData.png");
  background-size: cover;
  background-repeat: no-repeat;
  width: 150px;
  height: 150px;
}

.empty-middle-table .empty-img {
  width: 70px;
  height: 70px;
}

.dialog-footer {
  text-align: right;
}

/* 新增：弹窗样式 */
.form-label {
  font-weight: bold;
  margin-bottom: 8px;
  display: block;
  color: #333;
}

.mb-3 {
  margin-bottom: 16px;
}

.custom-range-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-separator {
  color: #666;
}

.total-rows-text {
  color: #909399;
  font-size: 12px;
}

.warning-text {
  color: #e6a23c;
  font-size: 12px;
  background-color: #fdf6ec;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #fde2a9;
}

/* 框选矩形 */
.box-select-rectangle {
  position: absolute;
  border: 1px dashed #409eff;
  background: rgba(64, 158, 255, 0.1);
  pointer-events: none;
  z-index: 10;
}

/* 框选拖拽中行高亮 */
:deep(.ag-row.box-select-highlight) {
  background-color: rgba(64, 158, 255, 0.15) !important;
}

/* 框选拖拽中禁止文本选中 */
:deep(.ag-body-viewport.box-selecting) {
  user-select: none;
  -webkit-user-select: none;
}
</style>